import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import HBetMatch from "../HBetMatch";
import HBetMatchLeagues from "../HBetMatchLeagues";
import HbetDateTabs from "../HbetDateTabs";
import LeagueFilterV15 from "../LeagueFilterV15";
import HBetOddsAndBookmaker from "../HBetOddsAndBookmaker";
import LoadMoreButton from "../LoadMoreButton";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
} from "@/lib/redux/reducers/actionTypes";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV15 = ({ type = "all" }) => {

  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorOnPrimary,
    colorBorder,
    colorTextMuted,
    colorAccent,
    colorForeground60,
    colorTabHover,
    hasDataConfig,
  } = useCompConfig();

  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
        ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
        ...(colorBorder != null && { "--theme-border": colorBorder }),
        ...(colorBorder != null && { "--theme-btn-border": colorBorder }),
        ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
        ...(colorAccent != null && { "--theme-accent": colorAccent }),
        ...(colorForeground60 != null && { "--theme-foreground-60": colorForeground60 }),
        ...(colorTabHover != null && { "--theme-tab-hover": colorTabHover }),
      }
    : {};

  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    [],
  );

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `Tỷ lệ ${item.shortName}`,
      })),
    [],
  );

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayString);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  useEffect(() => {
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();
  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => { clearAllData(); };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [firstLoad, selectedDate, loading, filteredLeaguesWithMatches, todayString]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20);
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20);
  };

  return (
    <div
      className="kbv-hbet-main min-h-screen"
      style={themeStyle}
    >
      <div className="mx-auto max-w-[1240px] px-1 md:px-[12px]">

      <div className="hidden md:flex md:flex-col md:gap-2 mt-4 mb-[8px] max-w-full overflow-visible">
        <div className="md:grid md:grid-cols-[minmax(0,1fr)_200px] md:gap-x-4 md:items-center">
          {/* Date Tabs */}
          <div className="min-w-0">
            <HbetDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          </div>
          {/* League Filter */}
          <div className="w-[200px] md:mb-6">
            <LeagueFilterV15
              selectedIds={selectedLeagueIds}
              onChange={handleLeagueFilterChange}
              className="w-full"
              themeStyle={themeStyle}
            />
          </div>
        </div>

        <div className="flex w-full min-w-0 justify-end pb-1 md:pb-2">
          <HBetOddsAndBookmaker
            extendValue={bookmarkerExtend}
            bookmakerValue={bookmarker}
            extendOptions={bookmakerExtendOptions}
            bookmakerOptions={bookmakerOptions}
            onExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            onBookmakerChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER, payload: val })
            }
            extendPlaceholder="Tỷ lệ HONGKONG"
            bookmakerPlaceholder="Tất cả"
          />
        </div>
      </div>

      {/* Mobile Controls */}
      <div className="md:hidden flex flex-col gap-2 pt-[8px] mb-[12px]">
        <div className="flex w-full min-w-0 justify-end">
          <HBetOddsAndBookmaker
            extendValue={bookmarkerExtend}
            bookmakerValue={bookmarker}
            extendOptions={bookmakerExtendOptions}
            bookmakerOptions={bookmakerOptions}
            onExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            onBookmakerChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER, payload: val })
            }
            extendPlaceholder="Tỷ lệ HONGKONG"
            bookmakerPlaceholder="Tất cả"
          />
        </div>
        <div className="flex gap-[10px]">
          <div className="w-1/2">
            <HbetDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              compact
            />
          </div>
          <div className="w-1/2">
            <LeagueFilterV15
              selectedIds={selectedLeagueIds}
              onChange={handleLeagueFilterChange}
              className="w-full"
              themeStyle={themeStyle}
            />
          </div>
        </div>
      </div>

      {/* Match List */}
      {loading && <Loading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          {filteredLeaguesWithMatches.length === 0 ? (
            <div className="p-10">
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            </div>
          ) : (
            <div className="flex flex-col gap-y-[4px] max-w-[1400px] mx-auto">
              {filteredLeaguesWithMatches.map(({ league, matches }) => (
                <HBetMatchLeagues
                  key={league?.id || league?.name}
                  league={league}
                  matches={matches}
                  render={(match, idx) => (
                    <HBetMatch
                      key={match?.id || idx}
                      match={match}
                      leagueName={league?.nameVN || league?.name}
                      index={idx}
                    />
                  )}
                />
              ))}
            </div>
          )}

          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={() => setDisplayLimit((prev) => prev + 20)}
            loadingComponent={<Loading />}
            hasMore={allFilteredMatches.length > displayLimit || hasMore}
          />
        </>
      )}
      </div>
    </div>
  );
};

export default KbvTiLeV15;
